"use client";

import { useEffect, useRef, useCallback, useState, memo, type CSSProperties } from "react";
import { useVirtualizer } from "@tanstack/react-virtual";
import type { ChannelEvent } from "../hooks/useMessages";
import type { Agent } from "../hooks/useAgents";
import type { WorkSession } from "../hooks/useWork";
import { MessageBubble } from "./MessageBubble";

const HEADER_HEIGHT = 37;
const MESSAGE_GAP = 4;

// ── Static styles (hoisted to avoid re-alloc per render) ─────────────────────

const loadingStyle: CSSProperties = {
  flex: 1,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  color: "var(--color-text-faint)",
  fontSize: "0.8rem",
};

const emptyStyle: CSSProperties = {
  ...loadingStyle,
  color: "var(--color-text-faintest)",
};

const centerColumnStyle: CSSProperties = {
  maxWidth: 768,
  marginLeft: "auto",
  marginRight: "auto",
  width: "100%",
};

const rowPaddingStyle: CSSProperties = {
  padding: "0 1rem",
};

const loadMoreWrapStyle: CSSProperties = {
  textAlign: "center" as const,
  padding: "12px 0",
};

const dividerStyle: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 8,
  margin: "8px 0",
  fontSize: "0.7rem",
  color: "var(--color-danger)",
  fontWeight: 600,
};

const dividerLineStyle: CSSProperties = {
  flex: 1,
  height: 1,
  background: "var(--color-danger)",
};

const dateSeparatorStyle: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 10,
  margin: "12px 0 4px",
  padding: "0 1rem",
};

const dateSeparatorLineStyle: CSSProperties = {
  flex: 1,
  height: 1,
  background: "var(--color-separator)",
};

const dateSeparatorLabelStyle: CSSProperties = {
  fontSize: "0.7rem",
  color: "var(--color-text-faintest)",
  fontWeight: 500,
  whiteSpace: "nowrap",
};

/** Format a date string as a human-readable date label (iMessage style). */
function formatDateLabel(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();

  // Strip time for day comparison
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const target = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const diffDays = Math.round((today.getTime() - target.getTime()) / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return "Today";
  if (diffDays === 1) return "Yesterday";
  if (diffDays < 7) {
    return date.toLocaleDateString(undefined, { weekday: "long" });
  }
  // Same year — "Wed, Feb 18"
  if (date.getFullYear() === now.getFullYear()) {
    return date.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
  }
  // Different year — "Wed, Feb 18, 2025"
  return date.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric", year: "numeric" });
}

/** Check if two date strings fall on different calendar days. */
function isDifferentDay(a: string, b: string): boolean {
  const da = new Date(a);
  const db = new Date(b);
  return da.getFullYear() !== db.getFullYear()
    || da.getMonth() !== db.getMonth()
    || da.getDate() !== db.getDate();
}

// ── Memoized row component ───────────────────────────────────────────────────
// Extracted so that React.memo can skip re-renders when a row's props haven't
// changed — critical inside a virtualizer where the parent re-renders on every
// scroll tick.

interface VirtualRowProps {
  msg: ChannelEvent;
  currentUserId: string | null;
  currentUserDisplayName: string | null;
  agents: Agent[];
  agentDisplayNames: Map<string, string>;
  userDisplayNames: Map<string, string>;
  workSession: WorkSession | undefined;
  onLoadWorkUpdates: (sessionId: string) => void;
  showDivider: boolean;
  showLoadMore: boolean;
  loadingOlder: boolean;
  onLoadMore: (() => void) | undefined;
  dateLabel: string | null;
}

const VirtualRow = memo(function VirtualRow({
  msg,
  currentUserId,
  currentUserDisplayName,
  agents,
  agentDisplayNames,
  userDisplayNames,
  workSession,
  onLoadWorkUpdates,
  showDivider,
  showLoadMore,
  loadingOlder,
  onLoadMore,
  dateLabel,
}: VirtualRowProps) {
  return (
    <div style={rowPaddingStyle}>
      {dateLabel && (
        <div style={dateSeparatorStyle}>
          <div style={dateSeparatorLineStyle} />
          <span style={dateSeparatorLabelStyle}>{dateLabel}</span>
          <div style={dateSeparatorLineStyle} />
        </div>
      )}
      {showLoadMore && (
        <div style={loadMoreWrapStyle}>
          <button
            onClick={onLoadMore}
            disabled={loadingOlder}
            style={{
              background: "none",
              border: "1px solid var(--color-border)",
              borderRadius: 6,
              padding: "6px 16px",
              fontSize: "0.75rem",
              color: loadingOlder ? "var(--color-text-faintest)" : "var(--color-text-muted)",
              cursor: loadingOlder ? "default" : "pointer",
            }}
          >
            {loadingOlder ? "Loading\u2026" : "Load older messages"}
          </button>
        </div>
      )}
      {showDivider && (
        <div style={dividerStyle}>
          <div style={dividerLineStyle} />
          <span>New messages</span>
          <div style={dividerLineStyle} />
        </div>
      )}
      <MessageBubble
        event={msg}
        isOwnMessage={msg.senderType === "user" && msg.senderUserId === currentUserId}
        currentUserId={currentUserId}
        agentName={
          msg.senderType === "agent" && msg.senderAgentId
            ? (agentDisplayNames.get(msg.senderAgentId)
              ?? agents.find((a) => a.id === msg.senderAgentId)?.name
              ?? null)
            : null
        }
        senderDisplayName={
          msg.senderType === "user" && msg.senderUserId
            ? (msg.senderUserId === currentUserId
              ? (currentUserDisplayName ?? userDisplayNames.get(msg.senderUserId) ?? null)
              : (userDisplayNames.get(msg.senderUserId) ?? null))
            : null
        }
        workSession={workSession}
        onLoadWorkUpdates={onLoadWorkUpdates}
      />
    </div>
  );
});

// ── Props ────────────────────────────────────────────────────────────────────

interface Props {
  messages: ChannelEvent[];
  loading: boolean;
  channelId: string | null;
  currentUserId: string | null;
  currentUserDisplayName: string | null;
  agents: Agent[];
  userDisplayNames: Map<string, string>;
  agentDisplayNames: Map<string, string>;
  workSessions: Map<string, WorkSession>;
  onLoadWorkUpdates: (sessionId: string) => void;
  hideChannelHeader?: boolean;
  lastReadEventId?: string | null;
  hasMore?: boolean;
  onLoadMore?: () => void;
}

// ── MessageList ──────────────────────────────────────────────────────────────

export function MessageList({ messages, loading, channelId, currentUserId, currentUserDisplayName, agents, userDisplayNames, agentDisplayNames, workSessions, onLoadWorkUpdates, hideChannelHeader, lastReadEventId, hasMore, onLoadMore }: Props) {
  const parentRef = useRef<HTMLDivElement>(null);
  const isAtBottomRef = useRef(true);
  const [loadingOlder, setLoadingOlder] = useState(false);

  // Track count before a "load more" so we can scroll-restore after prepend
  const countBeforeLoadRef = useRef<number | null>(null);

  const messagesRef = useRef(messages);
  messagesRef.current = messages;

  const getItemKey = useCallback(
    (index: number) => messagesRef.current[index]?.id ?? index,
    []
  );

  const virtualizer = useVirtualizer({
    count: messages.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 72,
    getItemKey,
    overscan: 10,
    gap: MESSAGE_GAP,
  });

  // Wrap measureElement in rAF to avoid flushSync-during-commit warnings
  // from React 19. The virtualizer's measureElement can trigger flushSync
  // when called from a ref callback during React's commit phase.
  // With block translation, the 1-frame measurement delay is harmless —
  // items flow naturally in the DOM, so stale estimates just mean items
  // briefly use estimated sizes before snapping, with no overlap or jumping.
  const measureElement = useCallback(
    (el: HTMLElement | null) => {
      if (el) requestAnimationFrame(() => virtualizer.measureElement(el));
    },
    [virtualizer]
  );

  // Track whether user is scrolled to bottom
  const handleScroll = useCallback(() => {
    const el = parentRef.current;
    if (!el) return;
    const distFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    isAtBottomRef.current = distFromBottom < 80;
  }, []);

  // Handle "Load more" button click
  const handleLoadMore = useCallback(async () => {
    if (!onLoadMore || loadingOlder) return;
    // Remember the current count so we can restore scroll after prepend
    countBeforeLoadRef.current = messagesRef.current.length;
    setLoadingOlder(true);
    await onLoadMore();
    setLoadingOlder(false);
  }, [onLoadMore, loadingOlder]);

  // After prepend from "Load more", scroll to keep the old first message in view.
  // Uses setTimeout to give the virtualizer a frame to recalculate positions.
  useEffect(() => {
    const prevCount = countBeforeLoadRef.current;
    if (prevCount !== null && messages.length > prevCount) {
      const prependedCount = messages.length - prevCount;
      countBeforeLoadRef.current = null;
      // Give virtualizer time to update its internal layout
      setTimeout(() => {
        virtualizer.scrollToIndex(prependedCount, { align: "start" });
      }, 0);
    }
  }, [messages.length, virtualizer]);

  // Count work update activity for scroll trigger
  let workUpdateCount = 0;
  for (const s of workSessions.values()) {
    workUpdateCount += s.updates.length;
  }

  // Reset scroll state when switching channels
  useEffect(() => {
    isAtBottomRef.current = true;
    setLoadingOlder(false);
    countBeforeLoadRef.current = null;
  }, [channelId]);

  // Auto-scroll to bottom when new messages or work updates arrive (if already at bottom)
  useEffect(() => {
    // Don't auto-scroll if we just prepended older messages
    if (countBeforeLoadRef.current !== null) return;

    if (isAtBottomRef.current) {
      requestAnimationFrame(() => {
        const el = parentRef.current;
        if (el) {
          el.scrollTop = el.scrollHeight;
        }
      });
    }
  }, [messages.length, workUpdateCount]);

  // Scroll to bottom on initial load or channel switch
  useEffect(() => {
    if (messages.length > 0) {
      setTimeout(() => {
        virtualizer.scrollToIndex(messages.length - 1, { align: "end" });
      }, 0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, channelId]);

  // Compute the index of the "New messages" divider.
  // Only show if the last-read message isn't the very last message
  // (i.e. there are genuinely unread messages after it).
  const dividerAfterIndex = (() => {
    if (!lastReadEventId) return -1;
    const idx = messages.findIndex((m) => m.id === lastReadEventId);
    if (idx >= 0 && idx < messages.length - 1) return idx;
    return -1;
  })();

  // Scroll to the divider on first render when there are unreads
  const scrolledToDivider = useRef(false);
  useEffect(() => {
    if (dividerAfterIndex >= 0 && !scrolledToDivider.current && messages.length > 0 && !loading) {
      scrolledToDivider.current = true;
      setTimeout(() => {
        virtualizer.scrollToIndex(dividerAfterIndex, { align: "start" });
      }, 50);
    }
  }, [dividerAfterIndex, messages.length, loading, virtualizer]);

  useEffect(() => {
    scrolledToDivider.current = false;
  }, [channelId]);

  if (loading && messages.length === 0) {
    return <div style={loadingStyle}>Loading messages...</div>;
  }

  if (messages.length === 0) {
    return <div style={emptyStyle}>No messages yet</div>;
  }

  const items = virtualizer.getVirtualItems();

  // ── Block-translation layout ─────────────────────────────────────────
  // Instead of positioning each item with its own absolute translateY,
  // we translate the entire rendered block to the first visible item's
  // start offset. Items inside the block flow naturally in the DOM
  // (position: relative), so their relative order is always correct even
  // if some measurements are temporarily stale. This eliminates the
  // "items overlapping or jumping" artifact that per-item absolute
  // positioning causes when scrolling upward through variable-height rows.
  const blockOffset = items.length > 0 ? items[0].start : 0;

  return (
    <div
      ref={parentRef}
      onScroll={handleScroll}
      style={{
        flex: 1,
        minHeight: 0,
        overflowY: "auto",
        overflowX: "hidden",
        minWidth: 0,
        position: "relative",
        paddingTop: hideChannelHeader ? 0 : HEADER_HEIGHT,
      }}
    >
      <div style={centerColumnStyle}>
        {/* Total-size container — gives the scrollbar the correct height */}
        <div
          style={{
            height: virtualizer.getTotalSize(),
            width: "100%",
            position: "relative",
          }}
        >
          {/* Block-translated chunk — all visible items as one translated block */}
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              transform: `translateY(${blockOffset}px)`,
            }}
          >
            {items.map((virtualRow) => {
              const msg = messages[virtualRow.index];
              const sessionId = msg.eventType === "work" ? (msg.body?.sessionId as string) : undefined;
              const workSession = sessionId ? workSessions.get(sessionId) : undefined;
              const showDivider = virtualRow.index === dividerAfterIndex + 1;
              const showLoadMore = virtualRow.index === 0 && !!hasMore;
              // Show date separator when this message is on a different day than the previous
              const prevMsg = virtualRow.index > 0 ? messages[virtualRow.index - 1] : null;
              const dateLabel =
                virtualRow.index === 0 || (prevMsg && isDifferentDay(prevMsg.createdAt, msg.createdAt))
                  ? formatDateLabel(msg.createdAt)
                  : null;
              return (
                <div
                  key={virtualRow.key}
                  data-index={virtualRow.index}
                  ref={measureElement}
                >
                  <VirtualRow
                    msg={msg}
                    currentUserId={currentUserId}
                    currentUserDisplayName={currentUserDisplayName}
                    agents={agents}
                    agentDisplayNames={agentDisplayNames}
                    userDisplayNames={userDisplayNames}
                    workSession={workSession}
                    onLoadWorkUpdates={onLoadWorkUpdates}
                    showDivider={showDivider}
                    showLoadMore={showLoadMore}
                    loadingOlder={loadingOlder}
                    onLoadMore={onLoadMore}
                    dateLabel={dateLabel}
                  />
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
